/**
 * Legal copy for public policy pages.
 * Draft for early-access testing — review with counsel before general availability.
 */

export const LEGAL_CONTACT_EMAIL = 'lexio@meet-base.com'
export const LEGAL_LAST_UPDATED = 'July 13, 2026'

export type LegalSection = {
  id: string
  title: string
  paragraphs: string[]
  bullets?: string[]
  paragraphsAfterBullets?: string[]
}

export const privacyPolicy: {
  title: string
  intro: string
  sections: LegalSection[]
} = {
  title: 'Privacy Policy',
  intro:
    'This Privacy Policy explains how Lexio ("we", "us", "our") collects, uses, and protects personal information when you use our English learning platform at lexio.meet-base.com and related services (the "Service"). We are in early access: this policy may be updated as the product matures.',
  sections: [
    {
      id: 'controller',
      title: '1. Who is responsible for your data',
      paragraphs: [
        'Lexio is operated as an early-access learning product. For privacy questions or to exercise your rights, contact us at the address in the Contact section below.',
      ],
    },
    {
      id: 'collect',
      title: '2. Information we collect',
      paragraphs: ['We collect information you provide and data generated through your use of the Service:'],
      bullets: [
        'Account details: email address, display name, and password (stored in hashed form). If you sign in with Google, we receive basic profile information from Google (such as your email and name) according to your Google account settings.',
        'Learning data: vocabulary you save, reading progress, review history, test results, learning preferences (for example native language, daily goals, and topic interests), and interactions with words and articles.',
        'AI-related content: words, phrases, and sentence context you submit when requesting explanations or practice exercises. This content is processed to provide the feature you asked for.',
        'Technical data: IP address, browser type, device information, and request logs needed to operate, secure, and troubleshoot the Service.',
        'Communications: messages you send us (for example support or privacy requests).',
      ],
    },
    {
      id: 'use',
      title: '3. How we use your information',
      paragraphs: ['We use personal information to:'],
      bullets: [
        'Create and manage your account and authenticate you.',
        'Provide core features: reading, vocabulary, spaced repetition, progress tracking, and AI-assisted explanations and practice.',
        'Personalize your experience (for example explanations in your native language and reading recommendations).',
        'Maintain security, prevent abuse, and debug technical issues.',
        'Improve the Service during early access, including fixing bugs and understanding how features are used in aggregate.',
        'Comply with legal obligations.',
      ],
    },
    {
      id: 'legal-bases',
      title: '4. Legal bases (EEA and UK users)',
      paragraphs: [
        'Where applicable data protection law requires a legal basis, we rely on: performance of our contract with you (providing the Service); our legitimate interests (security, product improvement, and analytics in aggregate form); and your consent where required (for example optional features or communications we may add later). You may withdraw consent at any time where processing is consent-based.',
      ],
    },
    {
      id: 'sharing',
      title: '5. Sharing and third-party services',
      paragraphs: [
        'We do not sell your personal information. We share data only as needed to run the Service:',
      ],
      bullets: [
        'Infrastructure providers that host our application and database (for example cloud hosting in the EU or other regions).',
        'Google, if you choose "Sign in with Google", subject to Google\'s privacy policy.',
        'AI providers (such as OpenAI) to generate word explanations and practice content. We send the minimum context needed for each request (for example the word, sentence context, and your language settings). We do not use your data to train third-party models on your behalf unless we explicitly tell you otherwise.',
        'Authorities when required by law or to protect rights, safety, and security.',
      ],
    },
    {
      id: 'cookies',
      title: '6. Cookies and local storage',
      paragraphs: [
        'We use essential cookies and similar technologies to keep you signed in (including secure refresh tokens), protect the Service, and remember necessary preferences. We may store some UI preferences in your browser\'s local storage (for example sort order or in-progress session state).',
        'We do not currently use third-party advertising or cross-site tracking cookies. If we add optional analytics or marketing cookies later, we will update this policy and, where required, ask for your consent.',
      ],
    },
    {
      id: 'retention',
      title: '7. How long we keep data',
      paragraphs: [
        'We retain your account and learning data while your account is active. If you delete your account in Settings, we mark your account as deleted and stop providing the Service to you. Some data may remain in backups for a limited period or where we must keep records for legal, security, or dispute-resolution purposes.',
        'Server logs are kept for a limited time appropriate for security and operations.',
      ],
    },
    {
      id: 'rights',
      title: '8. Your rights and choices',
      paragraphs: ['Depending on where you live, you may have the right to:'],
      bullets: [
        'Access, correct, or delete your personal information.',
        'Export your data (full self-service export is planned; contact us in the meantime).',
        'Object to or restrict certain processing.',
        'Withdraw consent where processing is based on consent.',
        'Lodge a complaint with your local data protection authority.',
      ],
      paragraphsAfterBullets: [
        'You can update many settings in your account. You can delete your account under Settings → Account. For other requests, email us using the contact details below.',
      ],
    },
    {
      id: 'security',
      title: '9. Security',
      paragraphs: [
        'We use technical and organizational measures appropriate for an early-access service, including encrypted connections (HTTPS), hashed passwords, and access controls. No method of transmission or storage is completely secure; we cannot guarantee absolute security.',
      ],
    },
    {
      id: 'transfers',
      title: '10. International transfers',
      paragraphs: [
        'Your data may be processed in countries other than your own, including where our infrastructure or AI providers operate. Where required, we use appropriate safeguards for cross-border transfers.',
      ],
    },
    {
      id: 'children',
      title: '11. Children',
      paragraphs: [
        'The Service is not directed at children under 16. We do not knowingly collect personal information from children under 16. If you believe a child has provided us data, contact us and we will take appropriate steps.',
      ],
    },
    {
      id: 'changes',
      title: '12. Changes to this policy',
      paragraphs: [
        'We may update this Privacy Policy as the Service evolves. We will post the revised version on this page and update the "Last updated" date. Material changes may be communicated by email or in-app notice where appropriate.',
      ],
    },
    {
      id: 'contact',
      title: '13. Contact',
      paragraphs: [
        `For privacy questions or requests, email ${LEGAL_CONTACT_EMAIL}. We aim to respond within a reasonable time.`,
      ],
    },
  ],
}

export const termsOfService: {
  title: string
  intro: string
  sections: LegalSection[]
} = {
  title: 'Terms of Service',
  intro:
    'These Terms of Service ("Terms") govern your access to and use of Lexio, an English learning platform available at lexio.meet-base.com (the "Service"). By creating an account or using the Service, you agree to these Terms. If you do not agree, do not use the Service.',
  sections: [
    {
      id: 'early-access',
      title: '1. Early access',
      paragraphs: [
        'Lexio is offered as an early-access product for testing and feedback. Features, content, availability, and these Terms may change without prior notice. The Service may contain bugs, downtime, or incomplete functionality. You use it at your own discretion during this phase.',
      ],
    },
    {
      id: 'eligibility',
      title: '2. Eligibility',
      paragraphs: [
        'You must be at least 16 years old and able to form a binding contract. You may use the Service only in compliance with applicable laws and these Terms.',
      ],
    },
    {
      id: 'account',
      title: '3. Your account',
      paragraphs: [
        'You are responsible for keeping your login credentials confidential and for activity under your account. Provide accurate information and notify us if you suspect unauthorized access. You may delete your account at any time in Settings.',
      ],
    },
    {
      id: 'service',
      title: '4. The Service',
      paragraphs: [
        'Lexio helps you learn English through reading, vocabulary building, spaced repetition, tests, and AI-assisted explanations and practice. We may add, change, or remove features during early access.',
      ],
    },
    {
      id: 'acceptable-use',
      title: '5. Acceptable use',
      paragraphs: ['You agree not to:'],
      bullets: [
        'Use the Service for unlawful, harmful, or abusive purposes.',
        'Attempt to gain unauthorized access to systems, accounts, or data.',
        'Scrape, overload, or interfere with the Service or its infrastructure.',
        'Reverse engineer or circumvent technical limits except where law permits.',
        'Upload or generate content that infringes others\' rights or violates applicable law.',
        'Share access credentials or resell access to the Service without permission.',
      ],
    },
    {
      id: 'ai-content',
      title: '6. AI-generated content',
      paragraphs: [
        'Some explanations, exercises, and other content are generated with AI. AI output may be inaccurate, incomplete, or inappropriate for your specific situation. It is provided for language learning support only and is not professional, legal, medical, or other expert advice. Always verify important information from authoritative sources.',
      ],
    },
    {
      id: 'ip',
      title: '7. Intellectual property',
      paragraphs: [
        'We own or license the Service, including software, design, branding, and curated learning content. You receive a limited, personal, non-exclusive, non-transferable license to use the Service for your own learning. You retain rights in content you submit; you grant us a license to host, process, and display it as needed to operate the Service.',
      ],
    },
    {
      id: 'privacy',
      title: '8. Privacy',
      paragraphs: [
        'Our Privacy Policy explains how we handle personal data. By using the Service, you acknowledge that we process data as described there.',
      ],
    },
    {
      id: 'termination',
      title: '9. Suspension and termination',
      paragraphs: [
        'We may suspend or terminate access if you violate these Terms, if required by law, or to protect the Service and other users. You may stop using the Service at any time. Provisions that by nature should survive (including disclaimers and limitations of liability) will survive termination.',
      ],
    },
    {
      id: 'disclaimer',
      title: '10. Disclaimer of warranties',
      paragraphs: [
        'THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT UNINTERRUPTED, ERROR-FREE, OR SECURE OPERATION.',
      ],
    },
    {
      id: 'liability',
      title: '11. Limitation of liability',
      paragraphs: [
        'TO THE MAXIMUM EXTENT PERMITTED BY LAW, LEXIO AND ITS OPERATORS WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF DATA, PROFITS, OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY FOR ANY CLAIM RELATING TO THE SERVICE IS LIMITED TO THE GREATER OF (A) AMOUNTS YOU PAID US FOR THE SERVICE IN THE TWELVE MONTHS BEFORE THE CLAIM, OR (B) EUR 50. SOME JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS; IN THOSE CASES, LIMITATIONS APPLY TO THE FULLEST EXTENT PERMITTED.',
      ],
    },
    {
      id: 'changes',
      title: '12. Changes to these Terms',
      paragraphs: [
        'We may update these Terms. Continued use after the effective date of updated Terms constitutes acceptance. If changes are material, we may provide additional notice.',
      ],
    },
    {
      id: 'law',
      title: '13. Governing law',
      paragraphs: [
        'These Terms are governed by the laws applicable to the operator of Lexio, without regard to conflict-of-law rules. Dispute resolution details will be specified before general public launch if they differ from this early-access version.',
      ],
    },
    {
      id: 'contact',
      title: '14. Contact',
      paragraphs: [
        `Questions about these Terms: ${LEGAL_CONTACT_EMAIL}.`,
      ],
    },
  ],
}
